import { Card, Group, Progress, Text, Tooltip } from '@mantine/core';
import { useHeartRateStore } from '@store/useHeartRateStore';
import { getDeviceStatus } from '@utils';
import { LucideIcon } from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { formatState, StatusKey } from '../../../../utils/formatState';
import { useUserDevices } from '../../../../hooks/useUserDevices';
import { useDeviceConnectionsStatus } from '../../../../hooks/useDeviceConnectionsStatus';
import { useSession } from '../../../../hooks/useSession';

interface StatProps {
  label: string;
  value: string | number;
  icon: React.ComponentType<any>;
  highlight?: boolean;
  isHeartBeating?: boolean;
  baselineInProgress?: boolean;
  totalDuration?: number;
  currentDuration?: number;
  isExperienceInProgress?: boolean;
}

export const Stat = ({
  label,
  value,
  icon,
  highlight = false,
  isHeartBeating = false,
  baselineInProgress = false,
  totalDuration,
  currentDuration,
  isExperienceInProgress = false,
}: StatProps) => {
  // Check if this is the heart rate stat
  const isHeartRateStat = label === 'Ritmo cardiaco attuale';
  const isDurationStat = label === 'Durata';

  // Calculate progress percentage for duration
  const getProgressPercentage = () => {
    if (!isDurationStat || !totalDuration || !currentDuration) return 0;
    const percentage =
      ((totalDuration - currentDuration) / totalDuration) * 100;
    return percentage;
  };

  // Format time remaining for tooltip
  const formatTimeRemaining = () => {
    if (!isDurationStat || !currentDuration) return '';

    const hours = Math.floor(currentDuration / 3600);
    const minutes = Math.floor((currentDuration % 3600) / 60);
    const seconds = currentDuration % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Tooltip content based on label
  const getTooltipContent = (label: string) => {
    switch (label) {
      case 'Durata': {
        const timeRemaining = formatTimeRemaining();
        const baseMessage =
          "L'esperienza si conclude automaticamente al raggiungimento del tempo impostato.";
        return timeRemaining
          ? `Tempo rimanente: ${timeRemaining}\n${baseMessage}`
          : baseMessage;
      }
      case 'Livello':
        return "Livello di difficoltà dell'esperienza di performance. -2 (più facile) a 2 (più difficile). Il livello determina la sensibilità del biofeedback e la complessità degli scenari.";
      case 'Variazione':
        return 'Sensibilità del biofeedback: percentuale di variazione dei battiti cardiaci che attiva il cambio di intensità dello scenario. Esempio: 5% su 60 BPM = ±3 battiti per cambiare stato.';
      case 'Stato attuale':
        return 'Livello di intensità corrente dello scenario immersivo. Basato sulla differenza tra battiti attuali e baseline di riferimento.';
      case 'Baseline':
        return "Valore di riferimento dei battiti cardiaci a riposo. Misurato prima dell'esperienza per calcolare le variazioni durante l'esercizio. Standard: 60 secondi di misurazione.";
      case 'Ritmo cardiaco attuale':
        return 'Monitoraggio in tempo reale della frequenza cardiaca. Samsung Galaxy Watch: solo HR. Polar 360: HR + variabilità cardiaca (HRV).';
      default:
        return '';
    }
  };

  const tooltipContent = getTooltipContent(label);

  // Format value based on label
  const getFormattedValue = () => {
    switch (label) {
      case 'Variazione':
        return `${value}%`;
      case 'Stato':
        return formatState(value as StatusKey);
      case 'Durata':
        return totalDuration &&
          currentDuration &&
          currentDuration < totalDuration
          ? formatTimeRemaining()
          : value;
      case 'Baseline':
        return String(value);
      default:
        return String(value);
    }
  };

  // Format label based on type
  const getFormattedLabel = () => {
    switch (label) {
      case 'Stato':
        return isExperienceInProgress ? 'Stato corrente' : 'Stato iniziale';
      default:
        return label;
    }
  };

  return (
    <Tooltip
      label={tooltipContent}
      position='right'
      withArrow
      multiline
      w={300}
      disabled={!tooltipContent}
    >
      <Card
        shadow='sm'
        withBorder
        p='xs'
        radius='sm'
        bg={
          isHeartRateStat
            ? baselineInProgress
              ? 'var(--mantine-color-red-0)'
              : isHeartBeating
                ? 'var(--mantine-color-red-0)'
                : 'var(--mantine-color-gray-0)'
            : highlight
              ? 'var(--mantine-color-blue-0)'
              : 'white'
        }
        w={'100%'}
        style={{ cursor: tooltipContent ? 'help' : 'default' }}
      >
        <div style={{ position: 'relative' }}>
          <Group align='center' gap='sm'>
            {React.createElement(icon, {
              size: 16,
              className:
                isHeartRateStat && isHeartBeating ? 'heartbeat-icon' : '',
              style: {
                color: isHeartRateStat
                  ? isHeartBeating
                    ? 'red'
                    : 'gray'
                  : undefined,
              },
            })}
            <Text size='sm' c='dimmed'>
              {getFormattedLabel()}:
            </Text>
            <Text fw={600}>{getFormattedValue()}</Text>
          </Group>

          {/* Progress bar for duration stat */}
          {isDurationStat &&
            isExperienceInProgress &&
            totalDuration &&
            currentDuration &&
            currentDuration < totalDuration &&
            currentDuration > 0 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                }}
              >
                <Progress
                  value={getProgressPercentage()}
                  size='xs'
                  color='blue'
                  radius='sm'
                  style={{ height: '2px' }}
                />
              </div>
            )}
        </div>
      </Card>
    </Tooltip>
  );
};

export const StatGeneric = ({
  label,
  value,
  icon,
  tooltipContent = true,
}: {
  label: string;
  value: string | number;
  tooltipContent?: boolean;
  icon: React.ComponentType<any>;
}) => {
  return (
    <Card
      shadow='sm'
      withBorder
      p='xs'
      radius='sm'
      w='100%'
      style={{ cursor: tooltipContent ? 'help' : 'default' }}
    >
      <Group align='center' gap='sm'>
        {React.createElement(icon, {
          size: 16,
          className: '',
        })}
        <Text size='sm' c='dimmed'>
          {label}:
        </Text>
        <Text fw={600}>{value}</Text>
      </Group>
    </Card>
  );
};

export const HeartRateStat = ({
  label,
  tooltipContent = true,
  icon,
}: {
  label: string;
  tooltipContent?: boolean;
  icon: LucideIcon;
}) => {
  const { id } = useParams();
  const { session: currentExperience } = useSession(id);
  const userId = currentExperience?.user_id?.toString();
  const { devices: allDevices } = useUserDevices(userId);
  const { connectionStatus } = useDeviceConnectionsStatus(allDevices);

  // Merge devices with connection status
  const devices = allDevices.map(device => {
    const clientId = device.clientId ?? device.code;
    return {
      ...device,
      status:
        connectionStatus[clientId ?? ''] ?? device.status ?? 'disconnected',
    };
  });

  // Get the latest heart rate from the events array
  const dataState = useHeartRateStore(s => s.events);
  const latestEvent = dataState.at(-1);
  const latestHeartRate = latestEvent?.hr || 0;

  const isSensorConnected =
    getDeviceStatus(devices, 'smartwatch') === 'connected';

  const displayValue =
    latestHeartRate > 0 && isSensorConnected ? latestHeartRate : '--';

  return (
    <Card
      shadow='sm'
      withBorder
      p='xs'
      radius='sm'
      bg={
        isSensorConnected
          ? 'var(--mantine-color-red-0)'
          : 'var(--mantine-color-gray-0)'
      }
      w='100%'
      style={{ cursor: tooltipContent ? 'help' : 'default' }}
    >
      <Group align='center' gap='sm'>
        {React.createElement(icon, {
          size: 16,
          className: isSensorConnected ? 'heartbeat-icon' : '',
          style: {
            color: isSensorConnected ? 'red' : 'gray',
          },
        })}
        <Text size='sm' c='dimmed'>
          {label}:
        </Text>
        <Text fw={600}>{displayValue}</Text>
      </Group>
    </Card>
  );
};
